import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Check, CreditCard } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

const LicensePurchaseModal = ({ open, onOpenChange }) => {
  const { token } = useAuth();
  const [quantity, setQuantity] = useState(5);
  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);

  const monthlyPrice = 3;
  const yearlyPrice = 30;
  const unitPrice = plan === "monthly" ? monthlyPrice : yearlyPrice;
  const totalPrice = unitPrice * quantity;
  const savings = plan === "yearly" ? (monthlyPrice * 12 - yearlyPrice) * quantity : 0;

  const handlePurchase = async () => {
    if (quantity < 1) { toast.error("Minimum 1 license"); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API}/licenses/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity, plan, origin_url: window.location.origin }),
      });
      const data = await response.json();
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Purchase Patient Licenses</DialogTitle>
          <DialogDescription>Add more patient slots to your account</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Plan Selection */}
          <div className="grid grid-cols-2 gap-3">
            <Card
              className={`p-4 rounded-2xl cursor-pointer transition-all ${plan === "monthly" ? "border-primary ring-2 ring-primary/20" : "border-gray-200"}`}
              onClick={() => setPlan("monthly")}
              data-testid="plan-monthly"
            >
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary">$3</p>
                <p className="text-sm text-muted-foreground">per patient/month</p>
                {plan === "monthly" && <Check className="w-5 h-5 text-primary mx-auto mt-2" />}
              </div>
            </Card>
            <Card
              className={`p-4 rounded-2xl cursor-pointer transition-all ${plan === "yearly" ? "border-primary ring-2 ring-primary/20" : "border-gray-200"}`}
              onClick={() => setPlan("yearly")}
              data-testid="plan-yearly"
            >
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary">$30</p>
                <p className="text-sm text-muted-foreground">per patient/year</p>
                <Badge className="bg-green-100 text-green-700 text-xs mt-1">Save 17%</Badge>
                {plan === "yearly" && <Check className="w-5 h-5 text-primary mx-auto mt-1" />}
              </div>
            </Card>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label>Number of Licenses</Label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="rounded-full w-10 h-10">-</Button>
              <Input
                type="number" min="1" max="1000" value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="text-center text-lg font-bold rounded-xl w-24"
                data-testid="license-quantity"
              />
              <Button variant="outline" size="sm" onClick={() => setQuantity(quantity + 1)} className="rounded-full w-10 h-10">+</Button>
              <div className="flex gap-2 ml-auto">
                {[5, 10, 25, 50].map(n => (
                  <Button key={n} variant="ghost" size="sm" onClick={() => setQuantity(n)} className={`text-xs rounded-full ${quantity === n ? "bg-primary/10 text-primary" : ""}`}>{n}</Button>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <Card className="p-4 rounded-2xl bg-orange-50 border-orange-100">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">{quantity} license{quantity > 1 ? "s" : ""} x ${unitPrice}/{plan === "monthly" ? "mo" : "yr"}</span>
              <span className="text-lg font-bold text-secondary">${totalPrice}</span>
            </div>
            {savings > 0 && (
              <p className="text-xs text-green-600 font-medium">You save ${savings}/year vs monthly billing</p>
            )}
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={handlePurchase} disabled={loading} className="rounded-full bg-primary text-white hover:bg-primary/90" data-testid="checkout-btn">
            <CreditCard className="w-4 h-4 mr-2" />
            {loading ? "Redirecting..." : `Pay $${totalPrice}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LicensePurchaseModal;
